import {
  FormFieldProps,
  MultiChoiceFormFieldProps,
} from 'components/inspection/Form/types';
import { toLabelledInvolvementType } from 'shared/domain/company/mapping';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import { ProjectSetupTranslationKeys, FiltersTranslationKeys } from 'intl/keys';
import {
  InvolvementType,
  involvementTypes,
} from 'shared/domain/company/types/model';
import type { CompanyOnView } from 'shared/domain/company/types/view';
import { getCountryOptions } from 'shared/domain/country/countries';
import { EditableStandardEntityName } from 'shared/domain/fieldValue/fields';
import React, { memo, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { LabelledEntity } from 'shared/types/commonView';
import { toAddressesFieldsProps } from './model';
import { CompanyFieldsPresentational } from './presentational';
import { CompanyFormProps } from './types';

function CompanyForm({
  disableInvolvement,
  sites,
  isDialog,
  initialValues,
  type,
}: CompanyFormProps & { type: 'short' | 'long' }): React.ReactElement {
  const intl = useIntl();
  const isLongForm = type === 'long';

  const shortLabelFieldProps: FormFieldProps<CompanyOnView> = {
    formKey: 'shortLabel',
    required: true,
    labelId: ProjectSetupTranslationKeys.shortLabelFieldLabel,
    fieldName: 'text-field-shortLabel',
    localStorageKey: 'company-short-label',
    reserveSpaceForHelperText: !isDialog,
    dense: true,
    'data-qa': 'company_short_label_field',
    issueParams: null,
  };

  const longLabelFieldProps: FormFieldProps<CompanyOnView> & {
    defaultValue?: string;
    value?: string;
  } = {
    formKey: 'longLabel',
    required: true,
    labelId: ProjectSetupTranslationKeys.longLabelFieldLabel,
    fieldName: 'text-field-longLabel',
    localStorageKey: 'company-long-label',
    reserveSpaceForHelperText: !isDialog,
    dense: true,
    'data-qa': 'company_long_label_field',
    issueParams: null,
  };

  const taxNumberFieldProps: FormFieldProps<CompanyOnView> = {
    formKey: 'taxNumber',
    required: false,
    labelId: ProjectSetupTranslationKeys.taxNumberFieldLabel,
    fieldName: 'text-field-taxNumber',
    localStorageKey: 'company-tax-number',
    reserveSpaceForHelperText: !isDialog,
    dense: true,
    'data-qa': 'company_tax_number_field',
    issueParams: null,
  };
  const sitesFieldProps: MultiChoiceFormFieldProps<CompanyOnView> = {
    formKey: 'sites',
    required: true,
    labelId: FiltersTranslationKeys.filterTypeSite,
    fieldName: 'multiselect-sites',
    available: sites,
    entityName: EditableStandardEntityName.location,
    reserveSpaceForHelperText: !isDialog,
    dense: true,
    'data-qa': 'company_sites',
    issueParams: null,
  };

  const projectInvolvementTypeFieldProps: FormFieldProps<CompanyOnView> & {
    value?: {
      _id: InvolvementType;
      label: string;
    };
  } = useMemo(() => {
    const available = involvementTypes.map((value) =>
      toLabelledInvolvementType(value, intl)
    );
    return {
      formKey: 'projectInvolvementType',
      required: true,
      labelId: ProjectSetupTranslationKeys.involvementTypeFieldLabel,
      fieldName: 'singleselect-projectInvolvementType',
      value: initialValues.projectInvolvementType,
      disabled: disableInvolvement,
      available,
      getOptionLabel: (option: LabelledEntity): string =>
        formatTranslation(intl, unsafeTranslationKey(option.label) ),
      reserveSpaceForHelperText: !isDialog,
      dense: true,
      'data-qa': 'company_involvement_type_field',
      issueParams: null,
    };
  }, [
    intl,
    disableInvolvement,
    initialValues.projectInvolvementType,
    isDialog,
  ]);

  const addressesFieldsProps = useMemo(
    () => toAddressesFieldsProps(isDialog, getCountryOptions(intl.locale)),
    [isDialog, intl.locale]
  );

  return (
    <CompanyFieldsPresentational
      shortLabelFieldProps={shortLabelFieldProps}
      longLabelFieldProps={longLabelFieldProps}
      sitesFieldProps={sitesFieldProps}
      projectInvolvementTypeFieldProps={
        isLongForm ? projectInvolvementTypeFieldProps : undefined
      }
      taxNumberFieldProps={isLongForm ? taxNumberFieldProps : undefined}
      addressesFieldsProps={addressesFieldsProps}
      isDialog={isDialog}
    />
  );
}

function ShortCompanyForm(props: CompanyFormProps): React.ReactElement {
  return <CompanyForm {...props} type='short' />;
}

function LongCompanyForm(props: CompanyFormProps): React.ReactElement {
  return <CompanyForm {...props} type='long' />;
}

export const MemoLongCompanyForm = memo(LongCompanyForm);
export const MemoShortCompanyForm = memo(ShortCompanyForm);
